import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import logger from '@ayocore/platform/common/helpers/logger';
import type { RepoItem, RpcRepoClient } from '@ayocore/platform/server/rpc/types';

/** Нормализованная опция select'а: строковые value/label + исходная запись репозитория. */
export interface RepoOption<R extends RpcRepoClient> {
  value: string;
  label: string;
  /** Исходная запись репозитория. Отсутствует у предзагруженной initialOption. */
  item?: RepoItem<R>;
}

export interface UseRepositoryOptionsParams<R extends RpcRepoClient> {
  /** RPC-репозиторий (chainable query). */
  repo: R;
  displayField?: keyof RepoItem<R> & string;
  valueField?: keyof RepoItem<R> & string;
  getOptionLabel?: (item: RepoItem<R>) => string;
  getOptionValue?: (item: RepoItem<R>) => string | number;
  /** Пост-фильтр загруженных записей. */
  customFilter?: (items: RepoItem<R>[]) => RepoItem<R>[];
  /** Включить серверный поиск через repo.search() (по умолчанию true). */
  enableServerSearch?: boolean;
  /** Сколько записей грузить при старте (по умолчанию 50). */
  initialLimit?: number;
  /** Сколько записей показывать при поиске (по умолчанию 50). */
  searchLimit?: number;
  /** Debounce поиска, мс (по умолчанию 300). */
  searchDebounce?: number;
  /** Минимум символов для серверного поиска (по умолчанию 2). */
  minSearchLength?: number;
  /** Предзагруженная опция — гарантированно присутствует в списке с первого рендера. */
  initialOption?: { value: string; label: string };
  /**
   * Значения (id), для которых нужно гарантированно знать лейбл, даже если они НЕ
   * попали в загруженную страницу (напр. выбранный из URL партнёр вне первых
   * `initialLimit`). Резолвятся одним `whereIn(valueField, ids)` после первой
   * загрузки; результат — в `ensuredOptions`. См. {@link RepositoryCombobox}.
   */
  ensureValues?: string[];
}

export interface UseRepositoryOptionsResult<R extends RpcRepoClient> {
  /** Опции в формате {value,label,item}, с подмешанной initialOption. */
  options: RepoOption<R>[];
  /**
   * Опции, дотянутые по `ensureValues` (вне загруженной страницы) — для засева
   * лейблов выбранных значений. В список дропдауна не входят.
   */
  ensuredOptions: RepoOption<R>[];
  loading: boolean;
  error: string | null;
  /** Текущее значение строки поиска (для подсветки/скрытия статичных опций). */
  search: string;
  setSearch: (value: string) => void;
  /** Активен ли серверный поиск (debounce + repo.search). */
  hasServerSearch: boolean;
}

/**
 * useRepositoryOptions — переиспользуемое ядро «RPC-репозиторий → опции select'а»
 * (загрузка + серверный поиск + маппинг в {value,label}).
 *
 * Инкапсулирует загрузку записей из репозитория, серверный поиск с debounce, лимиты,
 * маппинг в {value,label,item} и мерж предзагруженной initialOption. Единый источник
 * истины для {@link RepositorySelect} и {@link RepositoryCombobox} — чтобы маппинг,
 * поиск и сортировка не расходились между компонентами.
 *
 * Когда использовать: строишь СВОЙ select-подобный компонент поверх репозитория и
 * хочешь переиспользовать загрузку/поиск/маппинг. Для готовых решений —
 * {@link RepositoryCombobox} (виджет) или createRepositoryFilter (фильтр таблицы).
 *
 * @example
 * const { options, loading, search, setSearch } = useRepositoryOptions({
 *   repo: useUserRepository().onlyHumanActive().orderByName('asc'),
 *   displayField: 'name',
 * });
 *
 * @see RepositoryCombobox
 * @see RepositorySelect
 */
export function useRepositoryOptions<R extends RpcRepoClient>({
  repo,
  displayField,
  valueField = 'id' as keyof RepoItem<R> & string,
  getOptionLabel,
  getOptionValue,
  customFilter,
  enableServerSearch = true,
  initialLimit = 50,
  searchLimit = 50,
  searchDebounce = 300,
  minSearchLength = 2,
  initialOption,
  ensureValues,
}: UseRepositoryOptionsParams<R>): UseRepositoryOptionsResult<R> {
  const [items, setItems] = useState<RepoItem<R>[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, searchDebounce);

  // Серверный поиск включён по умолчанию. У любого репозитория есть базовый
  // search() (no-op, если домен его не переопределил), поэтому отдельный
  // авто-детект по наличию метода не нужен — управляем явным флагом.
  const hasServerSearch = enableServerSearch;

  const loadOptions = async (searchTerm?: string) => {
    setError(null);
    setLoading(true);

    try {
      let query: R = repo;

      // Chainable query — apply server search if available and enough chars
      if (hasServerSearch && searchTerm && searchTerm.length >= minSearchLength) {
        query = query.search(searchTerm) as R;
      }

      // Apply limit for performance
      const limit = searchTerm ? searchLimit : initialLimit;
      if (typeof query.limit === 'function') {
        query = query.limit(limit) as R;
      }

      const data = (await query.fetch?.()) ?? [];

      const filteredData = customFilter
        ? customFilter(data as RepoItem<R>[])
        : (data as RepoItem<R>[]);

      setItems(filteredData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load options';
      logger.error('❌ Failed to load repository options:', err);
      setError(errorMessage);
      setItems([]);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  };

  // Серверный поиск при изменении debounced-значения (на маунте срабатывает с '')
  useEffect(() => {
    if (!hasServerSearch) return;

    if (debouncedSearch.length >= minSearchLength || debouncedSearch === '') {
      loadOptions(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, hasServerSearch, minSearchLength]);

  // Auto-detect display field if not provided
  const autoDisplayField = useMemo(() => {
    if (displayField) return displayField;
    if (items.length === 0) return 'id';

    const firstItem = items[0] as Record<string, unknown>;
    if (firstItem.name != null) return 'name';
    if (firstItem.title != null) return 'title';
    if (firstItem.label != null) return 'label';
    return 'id';
  }, [displayField, items]);

  // Маппинг записей репозитория в формат опций
  const mappedOptions = useMemo<RepoOption<R>[]>(() => {
    return items.map(item => {
      const record = item as Record<string, unknown>;
      const value = getOptionValue ? getOptionValue(item) : record[valueField];
      const label = getOptionLabel ? getOptionLabel(item) : record[autoDisplayField];

      return {
        value: String(value),
        label: String(label),
        item,
      };
    });
  }, [items, autoDisplayField, getOptionLabel, getOptionValue, valueField]);

  // Мерж initialOption — гарантирует наличие опции с первого рендера
  const options = useMemo<RepoOption<R>[]>(() => {
    if (!initialOption) return mappedOptions;
    const alreadyPresent = mappedOptions.some(opt => opt.value === initialOption.value);
    if (alreadyPresent) return mappedOptions;
    return [initialOption, ...mappedOptions];
  }, [mappedOptions, initialOption]);

  // Резолв лейблов для ensureValues, которых нет в загруженной странице (напр.
  // выбранный из URL партнёр вне первых initialLimit). Дотягиваем одним whereIn по id
  // после первой загрузки; requestedRef не даёт дёргать один id повторно.
  const [ensuredOptions, setEnsuredOptions] = useState<RepoOption<R>[]>([]);
  const requestedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!loadedOnce) return; // ждём первой загрузки — иначе зря дёргаем по id
    const values = ensureValues ?? [];
    const known = new Set(mappedOptions.map(o => o.value));
    const missing = values.filter(v => !known.has(v) && !requestedRef.current.has(v));
    if (missing.length === 0) return;

    missing.forEach(v => requestedRef.current.add(v));
    let cancelled = false;

    (async () => {
      try {
        // Числовые id приводим к number (целочисленные колонки), uuid оставляем строкой
        const queryValues = missing.map(v => (/^\d+$/.test(v) ? Number(v) : v));
        const query = repo.whereIn(valueField, queryValues) as R;
        const rows = ((await query.fetch?.()) ?? []) as RepoItem<R>[];
        const resolved = rows.map(item => {
          const record = item as Record<string, unknown>;
          const value = getOptionValue ? getOptionValue(item) : record[valueField];
          const label = getOptionLabel ? getOptionLabel(item) : record[autoDisplayField];
          return { value: String(value), label: String(label), item };
        });
        if (!cancelled && resolved.length > 0) {
          setEnsuredOptions(prev => {
            const have = new Set(prev.map(o => o.value));
            const add = resolved.filter(o => !have.has(o.value));
            return add.length > 0 ? [...prev, ...add] : prev;
          });
        }
      } catch (err) {
        logger.error('useRepositoryOptions: ensureValues resolve failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureValues, mappedOptions, loadedOnce]);

  return { options, ensuredOptions, loading, error, search, setSearch, hasServerSearch };
}

export default useRepositoryOptions;
